// Unified analytics dispatcher.
//
// Routes every event through 3 backends in parallel:
//   1. PostHog (already wired in index.html) — primary product analytics
//   2. Google Analytics 4 (gtag) — fires only when REACT_APP_GA4_ID is set
//   3. Microsoft Clarity — fires only when REACT_APP_CLARITY_KEY is set
//
// The wrappers are no-ops if the corresponding SDK isn't loaded yet, so
// missing env vars NEVER break the app — they only mute that channel.

let booted = false;

const GA4_ID = process.env.REACT_APP_GA4_ID || '';
const CLARITY_KEY = process.env.REACT_APP_CLARITY_KEY || '';

const ensureScript = (src) => {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement('script');
  s.async = true; s.src = src;
  document.head.appendChild(s);
};

export const bootAnalytics = () => {
  if (booted) return;
  booted = true;

  if (GA4_ID) {
    ensureScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA4_ID, { send_page_view: true });
  }

  if (CLARITY_KEY) {
    // Microsoft Clarity tracking snippet (loader pattern from official docs).
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1;
      t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', CLARITY_KEY);
  }
};

/** Fire a single event to every active backend. */
export const track = (eventName, properties = {}) => {
  try { if (window.posthog?.capture) window.posthog.capture(eventName, properties); } catch { /* */ }
  try { if (window.gtag) window.gtag('event', eventName, properties); } catch { /* */ }
  try { if (window.clarity) window.clarity('event', eventName); } catch { /* */ }
  // Also POST to backend for our own funnel dashboard (best-effort, fire-and-forget).
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/event`;
    const body = JSON.stringify({ event: eventName, properties, ts: Date.now() });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true });
    }
  } catch { /* */ }
};

/** Convenience: page-view event (with route + title). */
export const trackPageView = (path) => {
  track('page_view', { path, title: document.title });
};

// Canonical OTA funnel events — keep names stable so dashboards don't break.
export const Events = {
  HOME_VIEW: 'home_view',
  SEARCH_INITIATED: 'search_initiated',
  SEARCH_RESULTS_VIEWED: 'search_results_viewed',
  HOTEL_VIEWED: 'hotel_viewed',
  FLIGHT_VIEWED: 'flight_viewed',
  ROOM_SELECTED: 'room_selected',
  BOOKING_STARTED: 'booking_started',
  PAYMENT_INITIATED: 'payment_initiated',
  PAYMENT_COMPLETED: 'payment_completed',
  BOOKING_CONFIRMED: 'booking_confirmed',
  VOUCHER_DOWNLOADED: 'voucher_downloaded',
  CANCELLATION_REQUESTED: 'cancellation_requested',
  REFUND_COMPLETED: 'refund_completed',
};

// ────────── GA4 Enhanced Ecommerce ──────────
// These match the exact GA4 ecommerce schema so Google Ads conversion tracking
// works out-of-the-box. Each helper builds the `items` array and fires both
// the GA4 event and our internal track() for unified analytics.
const _ga4Item = (item) => ({
  item_id: item.id || item.item_id,
  item_name: item.name || item.item_name,
  item_category: item.service || item.category || 'travel',
  item_brand: item.provider || item.brand,
  price: Number(item.display_price ?? item.price ?? 0),
  quantity: 1,
});

export const ga4ViewItem = (item) => {
  const items = [_ga4Item(item)];
  if (window.gtag) window.gtag('event', 'view_item', { currency: 'INR', value: items[0].price, items });
  track('view_item', { items });
};

export const ga4SelectItem = (item) => {
  const items = [_ga4Item(item)];
  if (window.gtag) window.gtag('event', 'select_item', { items });
  track('select_item', { items });
};

export const ga4BeginCheckout = (item, total) => {
  const items = [_ga4Item(item)];
  if (window.gtag) window.gtag('event', 'begin_checkout', { currency: 'INR', value: total, items });
  track('begin_checkout', { items, value: total });
};

export const ga4Purchase = (transactionId, item, value) => {
  const items = [_ga4Item(item)];
  if (window.gtag) window.gtag('event', 'purchase', { transaction_id: transactionId, currency: 'INR', value, items });
  track('purchase', { transaction_id: transactionId, value, items });
};

export const ga4Refund = (transactionId, value) => {
  if (window.gtag) window.gtag('event', 'refund', { transaction_id: transactionId, currency: 'INR', value });
  track('refund', { transaction_id: transactionId, value });
};
